import { Button, Paper } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { Form, Formik } from "formik";
import { isEqual } from "lodash";
import moment from "moment";
import React, { useEffect, useRef } from "react";
import * as Yup from "yup";
import useAuth from "../../../../hooks/auth";
import { useApiQuery } from "../../../../hooks/use-api-query";
import { useInvisibleReCAPTCHA } from "../../../../hooks/useInvisibleReCAPTCHA";
import InvisibleReCAPTCHA from "../../../ui/InvisibleReCAPTCHA";
import { useQueryContext } from "../context";
import AdvancedFields from "./advanced-fields";
import BasicFields from "./basic-fields";
import CourtSelectField from "./court-select-field";
import "./form.css.scss";
import initialValues from "./initialValues";
import SpecificFields from "./specific-fields";

const generateValorErrorMessage = (type, value) => {
  const valueFormatted = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);

  return `Valor deve ser ${type} ou igual à ${valueFormatted}`;
};

const generateExpedidoErrorMessage = (type, date) => {
  return `Data deve ser ${type} ou igual à ${moment(date).format(
    "DD/MM/yyyy"
  )}`;
};

const validDate = (date) => moment(date).isValid();

const sanitizeParams = (params) =>
  Object.keys(params)
    .filter((key) => !key.match(/token/i))
    .reduce((memo, key) => ((memo[key] = params[key]), memo), {});

export default () => {
  const {
    params,
    updateParams,
    pagination,
    orderBy,
    updateStatus,
    setResult,
    queryStatus,
  } = useQueryContext();

  const { ref: captchaRef, executeAsync: executeCaptchaAsync } =
    useInvisibleReCAPTCHA();

  const isLoading = queryStatus === "loading";
  const formRef = useRef();

  const {
    user: {
      configs: { search: configs },
      modules,
    },
  } = useAuth();

  const { data: basicData, isLoading: basicIsLoading } = useApiQuery([
    "support",
    "basic_form",
  ]);

  const validationSchema = Yup.object({
    basic: Yup.object({
      valorGteq: Yup.number().test({
        name: "valorLessThanMax",
        message: () =>
          generateValorErrorMessage(
            "menor",
            formRef.current?.values?.basic?.valorLteq
          ),
        test: (value, { parent }) => {
          const { valorLteq } = parent;
          return !value || !valorLteq || value <= valorLteq;
        },
      }),
      valorLteq: Yup.number().test({
        name: "valorGreaterThanMin",
        message: () =>
          generateValorErrorMessage(
            "maior",
            formRef.current?.values?.basic?.valorGteq
          ),
        test: (value, { parent }) => {
          const { valorGteq } = parent;
          return !value || !valorGteq || value >= valorGteq;
        },
      }),
    }),
    advanced: Yup.object({
      expedidoGteq: Yup.date()
        .nullable()
        .typeError("Data inválida")
        .test({
          name: "expedidoLessThanMax",
          message: () =>
            generateExpedidoErrorMessage(
              "anterior",
              formRef.current?.values?.advanced?.expedidoLteq
            ),
          test: (value, { parent }) => {
            const { expedidoLteq } = parent;
            return (
              !validDate(value) ||
              !validDate(expedidoLteq) ||
              value <= expedidoLteq
            );
          },
        }),
      expedidoLteq: Yup.date()
        .nullable()
        .typeError("Data inválida")
        .test({
          name: "expedidoGreaterThanMin",
          message: () =>
            generateExpedidoErrorMessage(
              "posterior",
              formRef.current?.values?.advanced?.expedidoGteq
            ),
          test: (value, { parent }) => {
            const { expedidoGteq } = parent;
            return (
              !validDate(value) ||
              !validDate(expedidoGteq) ||
              value >= expedidoGteq
            );
          },
        }),
    }),
  });

  const handleSubmit = async (values, reset) => {
    const partialQuery = values || params;

    const fullQuery = {
      ...partialQuery,
      page: reset ? 1 : pagination.currentPage,
      perPage: pagination.perPage,
      sort_by: orderBy,
    };

    if (
      Object.keys(partialQuery).length === 0 ||
      isEqual(fullQuery, sanitizeParams(params))
    ) {
      return;
    }

    updateStatus("loading");

    const captchaToken = await executeCaptchaAsync();

    updateParams({ ...fullQuery, captchaToken });
  };

  const { data } = useApiQuery(
    [modules.includes("rpv") ? "rpvs" : "precatorios", sanitizeParams(params)],
    {
      extraParams: {
        captcha_token: params.captchaToken,
      },
      useQueryOptions: {
        enabled: Object.keys(params).length > 0,
        onError: () => updateStatus("error"),
      },
    }
  );

  useEffect(() => {
    data && setResult(data);
  }, [data]);

  useEffect(() => {
    handleSubmit(null);
  }, [pagination.currentPage, pagination.perPage, orderBy]);

  return (
    <>
      {basicIsLoading ? (
        <Skeleton variant="rect" height="100px" width="100%" />
      ) : (
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => handleSubmit(values, true)}
          validationSchema={validationSchema}
          innerRef={formRef}
        >
          {({ values, errors, isValid, setFieldValue, resetForm, dirty }) => {
            const basicAdvancedDisabled =
              values.basic.tribunais.length == 0 || values.specificFilter;

            return (
              <Form>
                <InvisibleReCAPTCHA ref={captchaRef} />

                <Paper style={{ marginBottom: "20px" }}>
                  <CourtSelectField
                    fieldsData={basicData}
                    onClear={() => resetForm()}
                  />
                </Paper>

                <Paper>
                  {configs?.basic.show && (
                    <BasicFields
                      disabled={basicAdvancedDisabled}
                      errors={errors.basic}
                      fieldsData={basicData}
                    />
                  )}

                  {configs?.advanced.show && (
                    <AdvancedFields
                      disabled={basicAdvancedDisabled}
                      values={values.advanced}
                      errors={errors.advanced}
                    />
                  )}

                  {configs?.specific.show && (
                    <SpecificFields
                      setFieldValue={setFieldValue}
                      dirty={dirty}
                    />
                  )}

                  <div className="q-form-buttons">
                    <Button
                      fullWidth
                      type="submit"
                      className="btn btn-primary btn-mercatorio q-button-query"
                      disabled={isLoading || !isValid}
                    >
                      Pesquisar
                    </Button>

                    <Button
                      fullWidth
                      onClick={resetForm}
                      className="btn btn-light btn-clear q-button-reset"
                      disabled={isLoading}
                    >
                      Limpar
                    </Button>
                  </div>
                </Paper>
              </Form>
            );
          }}
        </Formik>
      )}
    </>
  );
};
